import { StatelessSearchbarItemContext } from "components/community-search/search-bar/stateless-searchbar-item";
import * as React from "react";
import { useCanSeeReferralSourceOwners, usePreferences } from "store/selectors/hooks";
import { ReferenceDashboardAspect } from "store/slices/reference-dashboard";

type ReferenceSearchSortValue = {
    sort: string;
    sortDirection: string;
};

export type ReferenceSearchSortValues = {
    contact: ReferenceSearchSortValue;
    business: ReferenceSearchSortValue;
    community: ReferenceSearchSortValue;
    organization: ReferenceSearchSortValue;
};

interface ReferenceSearchSortProps {
    aspect: ReferenceDashboardAspect;
    values: ReferenceSearchSortValues;
    onChange: (
        aspect: ReferenceDashboardAspect,
        sort: string,
        sortDirection: string,
    ) => void;
}

interface SortOption {
    key: string;
    title: string;
    direction: "ASC" | "DESC";
    hidden?: boolean;
}

export const ReferenceSearchSort: React.FC<ReferenceSearchSortProps> = props => {
    const { aspect, values, onChange } = props;
    const closeCalloutCallback = React.useContext(StatelessSearchbarItemContext);

    const chooseOption = (sortOption: string, direction: "ASC" | "DESC") => {
        if (closeCalloutCallback) {
            closeCalloutCallback();
        }
        onChange(aspect, sortOption, direction);
    };

    const { showReferralCityOnMainTable, showReferralLastUpdatedDateOnMainTable } =
        usePreferences();
    const assignedToVisible = useCanSeeReferralSourceOwners();

    const availableSorts: Record<ReferenceDashboardAspect, SortOption[]> =
        React.useMemo(() => {
            return {
                contact: [
                    { key: "name", title: "Contact name (A-Z)", direction: "ASC" },
                    { key: "name", title: "Contact name (Z-A)", direction: "DESC" },
                    { key: "role", title: "Role/Title (A-Z)", direction: "ASC" },
                    { key: "role", title: "Role/Title (Z-A)", direction: "DESC" },
                    {
                        key: "updatedAt",
                        title: "Last updated (most recent)",
                        direction: "DESC",
                        hidden: !showReferralLastUpdatedDateOnMainTable,
                    },
                    {
                        key: "updatedAt",
                        title: "Last updated (oldest)",
                        direction: "ASC",
                        hidden: !showReferralLastUpdatedDateOnMainTable,
                    },
                    {
                        key: "organizationName",
                        title: "Organization name (A-Z)",
                        direction: "ASC",
                    },
                    {
                        key: "organizationName",
                        title: "Organization name (Z-A)",
                        direction: "DESC",
                    },
                    {
                        key: "organizationType",
                        title: "Organization type (A-Z)",
                        direction: "ASC",
                    },
                    {
                        key: "organizationType",
                        title: "Organization type (Z-A)",
                        direction: "DESC",
                    },
                    {
                        key: "city",
                        title: "City (A-Z)",
                        direction: "ASC",
                        hidden: !showReferralCityOnMainTable,
                    },
                    {
                        key: "city",
                        title: "City (Z-A)",
                        direction: "DESC",
                        hidden: !showReferralCityOnMainTable,
                    },
                    {
                        key: "numberOfReferrals",
                        title: "Number of referrals (most)",
                        direction: "DESC",
                    },
                    {
                        key: "numberOfReferrals",
                        title: "Number of referrals (least)",
                        direction: "ASC",
                    },
                    {
                        key: "mostRecentReferral",
                        title: "Most recent referral (latest)",
                        direction: "DESC",
                    },
                    {
                        key: "mostRecentReferral",
                        title: "Most recent referral (earliest)",
                        direction: "ASC",
                    },
                    {
                        key: "assignedTo",
                        title: "Assigned to (A-Z)",
                        direction: "ASC",
                        hidden: !assignedToVisible,
                    },
                    {
                        key: "assignedTo",
                        title: "Assigned to (Z-A)",
                        direction: "DESC",
                        hidden: !assignedToVisible,
                    },
                ],
                business: [
                    { key: "name", title: "Business name (A-Z)", direction: "ASC" },
                    { key: "name", title: "Business name (Z-A)", direction: "DESC" },
                    {
                        key: "updatedAt",
                        title: "Last updated (most recent)",
                        direction: "DESC",
                        hidden: !showReferralLastUpdatedDateOnMainTable,
                    },
                    {
                        key: "updatedAt",
                        title: "Last updated (oldest)",
                        direction: "ASC",
                        hidden: !showReferralLastUpdatedDateOnMainTable,
                    },
                    {
                        key: "organizationType",
                        title: "Organization type (A-Z)",
                        direction: "ASC",
                    },
                    {
                        key: "organizationType",
                        title: "Organization type (Z-A)",
                        direction: "DESC",
                    },
                    {
                        key: "city",
                        title: "City (A-Z)",
                        direction: "ASC",
                        hidden: !showReferralCityOnMainTable,
                    },
                    {
                        key: "city",
                        title: "City (Z-A)",
                        direction: "DESC",
                        hidden: !showReferralCityOnMainTable,
                    },
                    {
                        key: "numberOfReferrals",
                        title: "Number of referrals (most)",
                        direction: "DESC",
                    },
                    {
                        key: "numberOfReferrals",
                        title: "Number of referrals (least)",
                        direction: "ASC",
                    },
                    {
                        key: "mostRecentReferral",
                        title: "Most recent referral (latest)",
                        direction: "DESC",
                    },
                    {
                        key: "mostRecentReferral",
                        title: "Most recent referral (earliest)",
                        direction: "ASC",
                    },
                    {
                        key: "assignedTo",
                        title: "Assigned to (A-Z)",
                        direction: "ASC",
                        hidden: !assignedToVisible,
                    },
                    {
                        key: "assignedTo",
                        title: "Assigned to (Z-A)",
                        direction: "DESC",
                        hidden: !assignedToVisible,
                    },
                ],
                community: [
                    { key: "name", title: "Community name (A-Z)", direction: "ASC" },
                    { key: "name", title: "Community name (Z-A)", direction: "DESC" },
                    {
                        key: "updatedAt",
                        title: "Last updated (most recent)",
                        direction: "DESC",
                        hidden: !showReferralLastUpdatedDateOnMainTable,
                    },
                    {
                        key: "updatedAt",
                        title: "Last updated (oldest)",
                        direction: "ASC",
                        hidden: !showReferralLastUpdatedDateOnMainTable,
                    },
                    {
                        key: "city",
                        title: "City (A-Z)",
                        direction: "ASC",
                        hidden: !showReferralCityOnMainTable,
                    },
                    {
                        key: "city",
                        title: "City (Z-A)",
                        direction: "DESC",
                        hidden: !showReferralCityOnMainTable,
                    },
                    {
                        key: "numberOfReferrals",
                        title: "Number of referrals (most)",
                        direction: "DESC",
                    },
                    {
                        key: "numberOfReferrals",
                        title: "Number of referrals (least)",
                        direction: "ASC",
                    },
                    {
                        key: "mostRecentReferral",
                        title: "Most recent referral (latest)",
                        direction: "DESC",
                    },
                    {
                        key: "mostRecentReferral",
                        title: "Most recent referral (earliest)",
                        direction: "ASC",
                    },
                ],
                organization: [
                    { key: "name", title: "Organization name (A-Z)", direction: "ASC" },
                    { key: "name", title: "Organization name (Z-A)", direction: "DESC" },
                    {
                        key: "updatedAt",
                        title: "Last updated (most recent)",
                        direction: "DESC",
                        hidden: !showReferralLastUpdatedDateOnMainTable,
                    },
                    {
                        key: "updatedAt",
                        title: "Last updated (oldest)",
                        direction: "ASC",
                        hidden: !showReferralLastUpdatedDateOnMainTable,
                    },
                    {
                        key: "city",
                        title: "City (A-Z)",
                        direction: "ASC",
                        hidden: !showReferralCityOnMainTable,
                    },
                    {
                        key: "city",
                        title: "City (Z-A)",
                        direction: "DESC",
                        hidden: !showReferralCityOnMainTable,
                    },
                    {
                        key: "numberOfReferrals",
                        title: "Number of referrals (most)",
                        direction: "DESC",
                    },
                    {
                        key: "numberOfReferrals",
                        title: "Number of referrals (least)",
                        direction: "ASC",
                    },
                    {
                        key: "mostRecentReferral",
                        title: "Most recent referral (latest)",
                        direction: "DESC",
                    },
                    {
                        key: "mostRecentReferral",
                        title: "Most recent referral (earliest)",
                        direction: "ASC",
                    },
                ],
            };
        }, [
            showReferralCityOnMainTable,
            showReferralLastUpdatedDateOnMainTable,
            assignedToVisible,
        ]);

    const currentSorts = availableSorts[aspect];
    const value = values[aspect];

    return (
        <div className="w-60">
            <h2 className="text-lg">Sort by</h2>
            <ul className="mt-2">
                {currentSorts.map(d =>
                    d.hidden ? null : (
                        <li
                            key={`${aspect}_${d.key}_${d.direction}`}
                            className={`cursor-pointer hover:text-brand-500 ${
                                value.sort === d.key &&
                                value.sortDirection === d.direction
                                    ? "font-bold"
                                    : ""
                            }`}
                            onClick={() => chooseOption(d.key, d.direction)}>
                            <input
                                className="mr-1"
                                type="radio"
                                checked={
                                    value.sort === d.key &&
                                    value.sortDirection === d.direction
                                }
                            />
                            {d.title}
                        </li>
                    ),
                )}
            </ul>
        </div>
    );
};
